import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Check } from "lucide-react";

const PLAN_ORDER = ["starter", "growth", "business"];

export default function Signup() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initial = PLAN_ORDER.includes(params.get("plan")) ? params.get("plan") : "growth";

  const [plans, setPlans] = useState([]);
  const [plan, setPlan] = useState(initial);
  const [form, setForm] = useState({
    name: "",
    company_name: "",
    email: "",
    phone: "",
    password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [trialDays, setTrialDays] = useState(30);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/billing/plans");
        setPlans((data.plans || []).filter((p) => p.key !== "enterprise"));
        setTrialDays(data.trial_days || 30);
      } catch { /* ignore */ }
    })();
  }, []);

  const submit = async () => {
    const name = form.name.trim();
    const email = form.email.trim().toLowerCase();
    const pw = form.password.trim();
    if (name.length < 2) return toast.error("Enter your full name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Enter a valid email.");
    if (pw.length < 8) return toast.error("Password must be at least 8 characters.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/billing/signup", {
        name,
        email,
        password: pw,
        company_name: form.company_name.trim(),
        phone: form.phone.trim(),
        plan_key: plan,
      });
      setUser(data);
      toast.success(`Welcome to DialFlow, ${data.name}! Your ${trialDays}-day trial has started.`);
      nav("/dashboard", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signup failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="signup-page">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/" className="font-heading font-bold text-lg flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-[#0055FF] rounded-full" />
            DialFlow
          </Link>
          <Link to="/login" className="text-sm text-slate-600 hover:text-slate-900" data-testid="signup-goto-login">
            Have an account? Sign in
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-10 grid md:grid-cols-[1fr,340px] gap-8">
        {/* Left: form */}
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-6 space-y-4">
            <div>
              <h1 className="text-2xl font-heading font-bold">Start your {trialDays}-day free trial</h1>
              <p className="text-sm text-slate-500 mt-1">
                No credit card. Cancel any time. Takes 30 seconds.
              </p>
            </div>

            {/* Plan pills */}
            <div className="space-y-2">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500">Plan</div>
              <div className="grid grid-cols-3 gap-2">
                {plans.map((p) => {
                  const active = plan === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setPlan(p.key)}
                      className={
                        "p-3 rounded-lg border text-left " +
                        (active
                          ? "border-[#0055FF] bg-blue-50/60 ring-1 ring-[#0055FF]"
                          : "border-slate-200 hover:border-slate-300")
                      }
                      data-testid={`signup-plan-${p.key}`}
                    >
                      <div className="font-heading font-bold text-sm">{p.name}</div>
                      <div className="mono text-[10px] text-slate-500">
                        Up to {p.employee_cap} employees
                      </div>
                      <div className="mt-1">
                        <span className="text-lg font-heading font-bold">
                          ₹{p.price_inr.toLocaleString("en-IN")}
                        </span>
                        <span className="text-[10px] text-slate-500"> /mo</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-slate-500">
                You won&apos;t be charged for {trialDays} days. Change plan any time.
              </div>
            </div>

            <label className="block space-y-1">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500">Your name</div>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Bipra Sarkar" data-testid="signup-name" />
            </label>
            <label className="block space-y-1">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500">Company / team name</div>
              <Input value={form.company_name} onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))} placeholder="Acme Services" data-testid="signup-company" />
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block space-y-1">
                <div className="mono text-[10px] uppercase tracking-widest text-slate-500">Work email</div>
                <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="you@company.com" data-testid="signup-email" />
              </label>
              <label className="block space-y-1">
                <div className="mono text-[10px] uppercase tracking-widest text-slate-500">Phone (optional)</div>
                <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+91..." data-testid="signup-phone" />
              </label>
            </div>
            <label className="block space-y-1">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500">Password (min 8 chars)</div>
              <Input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Strong password" data-testid="signup-password" />
            </label>

            <Button
              onClick={submit}
              disabled={submitting}
              className="w-full bg-[#0055FF] hover:bg-[#0040CC] h-11"
              data-testid="signup-submit"
            >
              {submitting ? "Creating your workspace…" : (<>Start {trialDays}-day free trial <ArrowRight className="w-4 h-4 ml-1" /></>)}
            </Button>
            <div className="text-[11px] text-slate-500 text-center">
              By continuing, you agree to our Terms &amp; Privacy Policy.
            </div>
          </CardContent>
        </Card>

        {/* Right: reassurance */}
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="mono text-[10px] uppercase tracking-widest text-emerald-700 mb-2">
              What you get today
            </div>
            <ul className="space-y-2 text-sm">
              {[
                `Full access for ${trialDays} days`,
                "No credit card required",
                "Attendance, tasks, WhatsApp inbox",
                "Cancel any time, no questions",
                "Your data, backed up daily",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">
            Questions? Email{" "}
            <a href="mailto:ccs.bud@gmail.com" className="text-[#0055FF]">
              ccs.bud@gmail.com
            </a>{" "}
            — we typically reply within a few hours.
          </div>
        </div>
      </div>
    </div>
  );
}
